import type { RouteRecordRaw } from 'vue-router';
import { defineStore } from 'pinia';
import { useSeedkitApi } from '@seedkit/core';
import type { ApiSeedKitAppModulesIamServiceApiUsersUserPermissionModuleViewModelDto } from 'app/api/data-contracts';
import type { SeedkitCore } from '@seedkit/core';
import { useAppStore } from '@seedkit/vue/stores';
import { useUserProfileStore } from '@seedkit/vue-administration-module/public';
import {
  POKOLENIE_MODULE_ACCESS,
  type PokolenieModuleSlug,
} from './pokolenie-module-access';

type ModuleRoutesEntry = {
  seedkit: SeedkitCore;
  slug: PokolenieModuleSlug;
  routes: RouteRecordRaw[];
};

function flattenPermissionNames(
  modules: ApiSeedKitAppModulesIamServiceApiUsersUserPermissionModuleViewModelDto[] | null | undefined,
): Set<string> {
  const set = new Set<string>();
  if (!modules) return set;
  for (const mod of modules) {
    for (const p of mod.permissions ?? []) {
      if (p?.name) set.add(p.name);
    }
  }
  return set;
}

function routeNeedsRegistration(entry: ModuleRoutesEntry): boolean {
  return entry.routes.some(
    (route) => route.name && !entry.seedkit.router.hasRoute(String(route.name)),
  );
}

export const usePermissionsStore = defineStore('pokolenie-permissions', {
  state: () => ({
    /** Имена IAM-операций (как в JSON / GetUserPermissions) */
    allowedOperationNames: new Set<string>() as Set<string>,
    rawModules: [] as ApiSeedKitAppModulesIamServiceApiUsersUserPermissionModuleViewModelDto[],
    loaded: false,
    loading: false,
    loadError: null as string | null,
    fetchInFlight: null as Promise<void> | null,
    /** Уже повешенные на роутер маршруты модулей */
    registeredSlugs: new Set<PokolenieModuleSlug>(),
    /** Все маршруты модулей, объявленные при boot (источник истины для повторной регистрации). */
    routeRegistry: [] as ModuleRoutesEntry[],
    pending: [] as ModuleRoutesEntry[],
    /** Счётчик для пересчёта меню после addRoute (hasRoute не реактивен). */
    routeRegistrationStamp: 0,
  }),

  getters: {
    isAdmin(): boolean {
      const app = useAppStore();
      return Boolean((app as { userInfo?: { isAdmin?: boolean } }).userInfo?.isAdmin);
    },
  },

  actions: {
    /**
     * Пользователь имеет хотя бы одну из операций (после загрузки прав).
     */
    hasAnyOperation(names: string[]): boolean {
      if (this.isAdmin) return true;
      if (!this.loaded) return false;
      if (!names.length) return this.allowedOperationNames.size > 0;
      return names.some((n) => this.allowedOperationNames.has(n));
    },

    /** Есть ли у пользователя конкретная IAM-операция (имя из GetUserPermissions). */
    hasOperation(name: string): boolean {
      return this.hasAnyOperation([ name ]);
    },

    isModuleAccessible(slug: PokolenieModuleSlug): boolean {
      const rule = POKOLENIE_MODULE_ACCESS[ slug ];
      if (!rule) return false;
      if (slug === 'presentation') {
        if (this.isAdmin) return true;
        return this.loaded && this.allowedOperationNames.size > 0;
      }
      return this.hasAnyOperation(rule.anyOf);
    },

    applyPermissionModules(
      modules: ApiSeedKitAppModulesIamServiceApiUsersUserPermissionModuleViewModelDto[] | null | undefined,
    ) {
      this.rawModules = modules ?? [];
      this.allowedOperationNames = flattenPermissionNames(modules);
      this.loaded = true;
      this.loadError = null;
    },

    rememberModuleRoutes(seedkit: SeedkitCore, slug: PokolenieModuleSlug, routes: RouteRecordRaw[]) {
      if (!this.routeRegistry.some((entry) => entry.slug === slug)) {
        this.routeRegistry.push({ seedkit, slug, routes });
      }
    },

    registerAccessibleModuleRoutes(): boolean {
      let addedAny = false;

      for (const item of this.routeRegistry) {
        if (!this.isModuleAccessible(item.slug)) {
          continue;
        }

        if (!routeNeedsRegistration(item)) {
          this.registeredSlugs.add(item.slug);
          continue;
        }

        item.routes.forEach((route) => item.seedkit.router.addRoute(route));
        this.registeredSlugs.add(item.slug);
        addedAny = true;
      }

      this.pending = this.pending.filter(
        (item) => !this.isModuleAccessible(item.slug) || routeNeedsRegistration(item),
      );

      if (addedAny) {
        this.routeRegistrationStamp += 1;
      }

      return addedAny;
    },

    async resolveUserId(): Promise<string | undefined> {
      const app = useAppStore() as { userInfo?: { id?: string } };
      if (app.userInfo?.id) return app.userInfo.id;

      const profile = useUserProfileStore();
      if (profile.userProfile?.id) return profile.userProfile.id;

      try {
        await profile.loadUserProfile();
        return profile.userProfile?.id;
      } catch {
        return undefined;
      }
    },

    async fetchByUserId(userId: string) {
      this.loadError = null;
      this.loading = true;
      try {
        const api = useSeedkitApi('Users');
        const { data } = await api.usersGetUserPermissions(userId);
        this.applyPermissionModules(data ?? []);
        this.registerAccessibleModuleRoutes();
      } catch (e) {
        this.loadError = e instanceof Error ? e.message : 'Ошибка загрузки прав';
        this.loaded = true;
        this.allowedOperationNames = new Set();
        this.registerAccessibleModuleRoutes();
      } finally {
        this.loading = false;
        this.routeRegistrationStamp += 1;
      }
    },

    clear() {
      this.allowedOperationNames = new Set();
      this.rawModules = [];
      this.loaded = false;
      this.loading = false;
      this.loadError = null;
      this.fetchInFlight = null;
      this.registeredSlugs.clear();
      this.pending = [];
    },

    /**
     * Регистрация маршрутов модуля: сразу, если прав достаточно, иначе — в очередь до fetchByUserId.
     */
    registerModuleRoutes(seedkit: SeedkitCore, slug: PokolenieModuleSlug, routes: RouteRecordRaw[]) {
      const entry: ModuleRoutesEntry = { seedkit, slug, routes };
      this.rememberModuleRoutes(seedkit, slug, routes);

      if (this.isModuleAccessible(slug) && routeNeedsRegistration(entry)) {
        routes.forEach((route) => seedkit.router.addRoute(route));
        this.registeredSlugs.add(slug);
        this.routeRegistrationStamp += 1;
        return;
      }

      if (this.registeredSlugs.has(slug) && !routeNeedsRegistration(entry)) {
        return;
      }

      if (!this.pending.some((item) => item.slug === slug)) {
        this.pending.push(entry);
      }

      void this.ensureFetchQueued();
    },

    flushPendingRoutes() {
      this.registerAccessibleModuleRoutes();
    },

    async _ensureFetchImpl(): Promise<void> {
      const id = await this.resolveUserId();
      if (!id) {
        return;
      }
      await this.fetchByUserId(id);
    },

    /** Загрузка прав и регистрация маршрутов модулей (один запрос на всех ожидающих). */
    async syncPermissionsAfterAuth(): Promise<void> {
      const id = await this.resolveUserId();
      if (!id) {
        this.loaded = true;
        this.registerAccessibleModuleRoutes();
        return;
      }
      await this.fetchByUserId(id);
    },

    /** Загрузка прав и регистрация маршрутов модулей (один запрос на всех ожидающих). */
    async ensureFetchQueued(): Promise<void> {
      if (this.loaded) {
        this.registerAccessibleModuleRoutes();
        return;
      }
      if (this.fetchInFlight) {
        await this.fetchInFlight;
        this.registerAccessibleModuleRoutes();
        return;
      }
      this.fetchInFlight = this._ensureFetchImpl();
      try {
        await this.fetchInFlight;
      } finally {
        this.fetchInFlight = null;
        this.registerAccessibleModuleRoutes();
      }
    },
  },
});
